import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule } from '@ngx-translate/core';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { upperFirst } from 'lodash';
import { imageExtensionValidator } from '../../validators/image-extension.validator';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { ICustomFile } from '../file-uploader/file-uploader.interface';
import { MatErrorComponent } from '../mat-error/mat-error.component';
import { SideDialogComponent } from '../side-dialog/side-dialog.component';
import {
  EBugReportStatus,
  EBugReportType,
  IBugReport,
  IBugReportDialogData,
  IBugReportDialogForm,
} from './bug-report.interface';
import { BugReportService } from './bug-report.service';

@Component({
  imports: [
    SideDialogComponent,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    FileUploaderComponent,
  ],
  selector: 'app-bug-report-dialog',
  template: `
    <app-side-dialog (closeDialog)="onCloseClick()">
      <div dialogHeader>
        <h2>
          {{ 'shared.bugReport.' + form.value.type + 'Title' | translate }}
        </h2>
      </div>

      <div dialogContent class="overflow-hidden">
        <form [formGroup]="form" class="flex flex-col gap-4 custom-form">
          <mat-form-field>
            <mat-label>{{ 'shared.bugReport.type' | translate }}</mat-label>
            <mat-select formControlName="type" [hideSingleSelectionIndicator]="true">
              <!-- <mat-option [value]="EBugReportType.bug">{{ 'shared.bugReport.bug' | translate }}</mat-option> -->
              <mat-option [value]="EBugReportType.feedback">{{ 'shared.bugReport.feedback' | translate }}</mat-option>
              <mat-option [value]="EBugReportType.contact">{{ 'shared.bugReport.contact' | translate }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field class="flex-1 expandable-textarea-field">
            <mat-label>{{ 'shared.bugReport.message' | translate }}</mat-label>
            <textarea
              matInput
              cdkTextareaAutosize
              class="!flex !items-center"
              formControlName="message"
              cdkAutosizeMinRows="3"
              cdkAutosizeMaxRows="10"
            ></textarea>
            <mat-error [control]="form.get('description')" />
          </mat-form-field>

          <app-file-uploader
            size="big"
            [imageId]="imageId ?? null"
            [fileForm]="imageFileControl"
            [requiredPermission]="[EPermissions.imagesCreate]"
            (imageSelected)="onImageSelected($event)"
            (imageRemoved)="onImageRemoved()"
            #fileUploaderComponent
          />

          <div class="flex justify-end mb-4">
            <button mat-flat-button color="primary" (click)="onSubmit()" [disabled]="!form.valid">
              {{ 'shared.send' | translate }}
            </button>
          </div>
        </form>
      </div>
    </app-side-dialog>
  `,
})
export class BugReportDialogComponent {
  public readonly form: FormGroup<IBugReportDialogForm>;

  public imageId: string | null = null;
  public initialImageDeleted = false;

  public EBugReportType = EBugReportType;
  public EPermissions = EPermissions;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<BugReportDialogComponent>,
    private readonly bugReportService: BugReportService,
    private readonly imageUtilitiesService: ImageUtilitiesService,
    @Inject(MAT_DIALOG_DATA) public readonly data: IBugReportDialogData,
  ) {
    this.createBackdropClickSubscription();

    this.form = this.formBuilder.group({
      imageFile: [null, imageExtensionValidator()],
      message: ['', Validators.required],
      type: [data.type || 'Bug', Validators.required],
    }) as FormGroup<IBugReportDialogForm>;

    if (data.imageId) {
      this.imageId = data.imageId;
    }
  }

  public get imageFileControl(): FormControl<ICustomFile | null> | null {
    return this.form.get('imageFile') as FormControl<ICustomFile | null> | null;
  }

  public onImageSelected(event: { file: File; id: string }): void {
    this.deleteImages();
    this.imageId = event.id;
    this.imageFileControl?.setValue(event.file);
  }

  public onImageRemoved(): void {
    this.initialImageDeleted = true;
    this.imageId = null;
    this.imageFileControl?.setValue(null);

    setTimeout(() => {
      this.imageId = null;
    });
  }

  public onCloseClick(): void {
    this.deleteImages();
    this.dialogRef.close();
  }

  public async onSubmit(): Promise<void> {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.value;
    const bugReport: IBugReport = {
      browserInfo: await this.bugReportService.getSystemInfo(),
      currentUrl: window.location.href,
      imageId: this.imageId || null,
      message: formValue.message!,
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      status: upperFirst(EBugReportStatus.new) as EBugReportStatus,
      type: upperFirst(formValue.type!) as EBugReportType,
    };

    this.dialogRef.close(bugReport);
  }

  private createBackdropClickSubscription(): void {
    this.dialogRef.backdropClick().subscribe(() => {
      this.onCloseClick();
    });
  }

  private deleteImages(): void {
    if (this.data.imageId && !this.initialImageDeleted) {
      this.initialImageDeleted = true;
      this.imageUtilitiesService.deleteImage(this.data.imageId).subscribe();
    }

    if (this.imageId && this.imageId !== this.data.imageId) {
      this.imageUtilitiesService.deleteImage(this.imageId).subscribe();
    }
  }
}
